import {
  useCreateMasterSop,
  useSLARulesOptions,
} from "@features/wx/master/hooks";
import {
  useOperationTypes,
  usePPEEquipment,
  useSafetyProtocols,
} from "@features/wx/settings/hooks";
import { StepHelpers } from "@shared/components/Stepper";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  AlertTriangle,
  Banknote,
  BarChart2,
  Check,
  ChevronRight,
  ClipboardList,
  Clock,
  Eye,
  FileText,
  HelpCircle,
  Package,
  Shield,
  UserRound,
} from "lucide-react";
import React, { useState } from "react";

interface SopReviewStepProps {
  state: any; // Updated to match project pattern
  onUpdateState: (updates: any) => void; // Updated to match project pattern
  onPrevious?: () => void; // Optional since stepper handles navigation
  onSubmit?: () => void; // Optional submission handler
  onSaveAsDraft?: () => void; // Optional save as draft handler
  submitLabel?: string; // Submit button label
  readOnly?: boolean;
  templateId: string;
  onStepSave?: (stepIndex: number, data: any, isDirty: boolean) => void;
  stepHelpers?: StepHelpers;
}

const SopReviewStep: React.FC<SopReviewStepProps> = ({
  state,
  onUpdateState,
  onPrevious,
  onSubmit,
  onSaveAsDraft,
  submitLabel = "Create SOP",
  readOnly,
  templateId,
  onStepSave,
  stepHelpers
}) => {
  const currency = useCurrency();
  const [expandedSections, setExpandedSections] = useState<string[]>([
    "basicInfo",
  ]);

  const { data: ppeEquipmentLookup = [] } = usePPEEquipment();
  const { data: safetyProtocolsLookup = [] } = useSafetyProtocols();
  const [previewMode, setPreviewMode] = useState(false);
  const createMasterSopDocuments = useCreateMasterSop();
  const { options: SLARulesOptions } = useSLARulesOptions();
  const { data: operationTypesData } = useOperationTypes();

  // Handle submission with hook integration
  const handleSubmit = async () => {
    try {
      await createMasterSopDocuments.mutateAsync({
        sopId: state.basicInfo?.sopId || templateId,
        reviewPublishUpdated: true,
        status: 0,
      });
      if (onStepSave) {
        await onStepSave(6, state, true); // step index 6 for review, pass entire state
      }

      // Clear localStorage to ensure fresh start for next SOP creation
      localStorage.removeItem("sop-builder-progress");
      if (templateId) {
        localStorage.removeItem(`sop-builder-progress-${templateId}`);
      }

      // Call the original onSubmit handler if provided
      if (onSubmit) {
        onSubmit();
      }
    } catch (error) {
      // Error handling can be added here if needed
    }
  };

  // Toggle section expansion
  const toggleSectionExpansion = (sectionId: string) => {
    setExpandedSections((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      } else {
        return [...prev, sectionId];
      }
    });
  };

  // Check if a section is expanded
  const isSectionExpanded = (sectionId: string) => {
    return expandedSections.includes(sectionId);
  };

  // Get status badges for each section
  const getStatusBadge = (section: string) => {
    switch (section) {
      case "basicInfo":
        return state.basicInfo?.sopName && state.basicInfo?.utilityType ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            Complete
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-md font-normal">
            Incomplete
          </Badge>
        );
      case "safety":
        return state.safety?.ppe?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            Complete
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-md font-normal">
            Not Configured
          </Badge>
        );
      case "tasks":
        return state.tasks?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            {state.tasks.length} Tasks
          </Badge>
        ) : (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-md font-normal">
            No Tasks
          </Badge>
        );
      case "materials":
        return state.materials?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            {state.materials.length} Items
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-md font-normal">
            No Materials
          </Badge>
        );
      case "readings":
        return state.readings?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            {state.readings.length} Readings
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-md font-normal">
            No Readings
          </Badge>
        );
      case "resources":
        return state.resources?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            {state.resources.length} Resources
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-md font-normal">
            Not Configured
          </Badge>
        );
      case "contacts":
        return state.contacts?.length > 0 ? (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
            {state.contacts.length} Contacts
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 rounded-md font-normal">
            Not Added
          </Badge>
        );
      default:
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100 rounded-md font-normal">
            Not Completed
          </Badge>
        );
    }
  };

  // Calculate estimated duration
  const calculateTotalDuration = () => {
    if (!state.tasks || state.tasks.length === 0) return 0;
    return state.tasks.reduce(
      (total: number, task: any) => total + (task.duration || 0),
      0
    );
  };

  const getReadings = () => {
    const baseReadings = (state.readings || []).map((item: any) => ({
      ...item,
    }));

    const unitReadings = state.basicInfo?.unitReadings || [];

    // list of names from base
    const existingNames = new Set(
      baseReadings.map((item: any) => item.name?.toLowerCase())
    );

    // If name exists in both base and unit, mark base item as isFromUnit: true
    baseReadings.forEach((item: any) => {
      const nameKey = item.name?.toLowerCase();
      item.isFromUnit = unitReadings.some(
        (u: any) => u.name?.toLowerCase() === nameKey
      );
    });

    // include only those unit readings whose name doesn't exist in base
    const filteredUnitReadings = unitReadings
      .filter((item: any) => !existingNames.has(item.name?.toLowerCase()))
      .map((item: any) => ({
        ...item,
        isFromUnit: true,
      }));

    const readings = [...baseReadings, ...filteredUnitReadings];

    return readings;
  };

  // Format duration in hours and minutes
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  // Calculate estimated cost
  const estimatedLabourCost = state.basicInfo?.estimatedLabourCost || 0;

  // Check if we have any validation warnings
  const hasValidationWarnings =
    !state.basicInfo?.sopName ||
    !state.basicInfo?.utilityType ||
    !state.basicInfo?.type;
  const getPPEDisplay = (ppeId) => {
    // Handle if it's already a string/name
    if (typeof ppeId === "string" && isNaN(Number(ppeId))) {
      return ppeId;
    }

    // Convert to number for ID lookup
    const id = typeof ppeId === "string" ? parseInt(ppeId, 10) : ppeId;

    // Find the PPE item by ID
    const ppeItem = ppeEquipmentLookup.find((item) => item.id === id);

    return ppeItem ? ppeItem.name : `PPE ID: ${id}`;
  };

  const getSafetyProtocolDisplay = (protocolId) => {
    // Handle if it's already a string/name
    if (typeof protocolId === "string" && isNaN(Number(protocolId))) {
      return protocolId;
    }

    // Convert to number for ID lookup
    const id =
      typeof protocolId === "string" ? parseInt(protocolId, 10) : protocolId;

    // Find the protocol item by ID
    const protocolItem = safetyProtocolsLookup.find((item) => item.id === id);

    return protocolItem ? protocolItem.name : `Protocol ID: ${id}`;
  };
  // Map helpDocuments to resource card format for display
  const helpDocuments = state.helpDocuments || [];
  const helpDocumentResources = (helpDocuments || []).map((doc: any) => ({
    id: doc.id,
    type: doc.type === 0 ? "instruction" : doc.type === 1 ? "video" : "file",
    name: doc.name || doc.typeDisplay || "Document",
    content: doc.description || doc.link || "",
    apiType: doc.type,
    existingId: doc.id,
  }));

  // Helper to get resource icon (copy from SopHelpStep)
  const getResourceIcon = (type: string) => {
    switch (type) {
      case "video":
        return <ChevronRight className="h-4 w-4" />;
      case "file":
        return <FileText className="h-4 w-4" />;
      case "instruction":
        return <ClipboardList className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Combine API and local resources for flat list display
  const allResources = [
    ...(helpDocumentResources || []),
    ...(state.resources || []),
  ];

  // Basic Info section display helper
  const renderBasicInfo = () => {
    const { basicInfo } = state;

    // ✅ Helper function to get display value (label if available, otherwise extract from coded value)
    const getDisplayValue = (value: string, label: string | undefined) => {
      if (label) return label;
      if (!value) return "—";

      // Handle coded values like "varuuun#10" - extract the part before #
      if (value.includes("#")) {
        return value.split("#")[0];
      }

      return value;
    };

    // ✅ Enhanced association mapping with better display
    const getAssociationDisplay = (value: string, label?: string) => {
      if (label) return label;

      const associationMap: { [key: string]: string } = {
        "0": "Consumer",
        "1": "Meter",
        "2": "Asset",
        "3": "Complaint",
        "4": "Generic Work Order",
      };

      return associationMap[value] || getDisplayValue(value, undefined);
    };

    // ✅ Enhanced utility type display
    const getUtilityTypeDisplay = (value: string, label?: string) => {
      return getDisplayValue(value, label);
    };

    // ✅ Enhanced type/subtype display
    const getTypeDisplay = (value: string, label?: string) => {
      return getDisplayValue(value, label);
    };

    const getSubtypeDisplay = (value: string, label?: string) => {
      return getDisplayValue(value, label);
    };

    // ✅ Enhanced SLA display - FIXED TO USE LABEL
    const getSLADisplay = (value: string, label?: string) => {
      if (label) return label;

      // Find the SLA option by value
      const slaOption = SLARulesOptions?.find(
        (option) => option.value.toString() == value
      );

      return slaOption ? slaOption.label : getDisplayValue(value, undefined);
    };

    // ✅ Enhanced tax display
    const getTaxDisplay = (value: string, label?: string) => {
      if (label) return label;

      // Handle format like "20#2" - extract the first part and add %
      if (value && value.includes("#")) {
        const taxValue = value.split("#")[0];
        return `${taxValue}%`;
      }

      return value ? `${value}%` : "—";
    };

    // ✅ Enhanced operation type display - FIXED TO USE LABEL
    const getOperationTypeDisplay = (value: string, label?: string) => {
      if (label) return label;

      // Find the operation type by key
      const operationType = operationTypesData?.find(
        (option) => option.key.toString() == value
      );

      return operationType
        ? operationType.value
        : getDisplayValue(value, undefined);
    };

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              SOP Name
            </h4>
            <p className="text-base font-medium mt-1">
              {basicInfo?.sopName || "—"}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Short Name
            </h4>
            <p className="text-base font-medium mt-1">
              {basicInfo?.shortName || "—"}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Utility Type
            </h4>
            <p className="text-base font-medium mt-1 capitalize">
              {getUtilityTypeDisplay(
                basicInfo?.utilityType,
                basicInfo?.utilityTypeLabel
              )}
            </p>
          </div>
        </div>

        <div>
          <h4 className="text-sm font-medium text-muted-foreground">
            Description
          </h4>
          <p className="text-base mt-1">{basicInfo?.description || "—"}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Type / Subtype
            </h4>
            <p className="text-base font-medium mt-1">
              {getTypeDisplay(basicInfo?.type, basicInfo?.typeLabel)} /{" "}
              {getSubtypeDisplay(basicInfo?.subType, basicInfo?.subTypeLabel)}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Service Association
            </h4>
            <div className="mt-1">
              <Badge
                variant="outline"
                className="border-blue-500 bg-blue-50 text-blue-700"
              >
                {getAssociationDisplay(
                  basicInfo?.associatedWith,
                  basicInfo?.associatedWithLabel
                )}
              </Badge>
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Operation Type
            </h4>
            <p className="text-base font-medium mt-1">
              {getOperationTypeDisplay(
                basicInfo?.operationType,
                basicInfo?.operationTypeLabel
              )}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 pt-1">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">SLA</h4>
            <p className="text-base font-medium mt-1">
              {getSLADisplay(basicInfo?.sla, basicInfo?.slaLabel)}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              SLA Hours
            </h4>
            <p className="text-base font-medium mt-1">
              {basicInfo?.slaHrs ? `${basicInfo.slaHrs} hrs` : "—"}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Labour Cost
            </h4>
            <p className="text-base font-medium mt-1">
              {currency} {basicInfo?.estimatedLabourCost || "—"}
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Tax Rate
            </h4>
            <p className="text-base font-medium mt-1">
              {getTaxDisplay(basicInfo?.taxRate, basicInfo?.taxRateLabel)}%
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Required Personnel
            </h4>
            <p className="text-base font-medium mt-1">
              {basicInfo?.requiredFieldforceCount || "—"} field technicians
            </p>
          </div>
          <div>
            <h4 className="text-sm font-medium text-muted-foreground">
              Service Number
            </h4>
            <p className="text-base font-medium mt-1">
              {basicInfo?.serviceNo || "—"}
            </p>
          </div>
        </div>

        {/* Asset Configuration Section - Enhanced Display */}
        {basicInfo?.associatedWith === "2" && ( // "2" is Asset
          <div className="border rounded-md p-3 bg-slate-50">
            <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Asset Configuration
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Asset Type
                </h4>
                <p className="text-base font-medium mt-1 capitalize">
                  {basicInfo?.customAssetType || basicInfo?.assetType || "—"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Action Type
                </h4>
                <p className="text-base font-medium mt-1 capitalize">
                  {basicInfo?.customActionType || basicInfo?.actionType || "—"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground">
                  Operation Type
                </h4>
                <p className="text-base font-medium mt-1 capitalize">
                  {basicInfo?.customOperationType ||
                    getOperationTypeDisplay(
                      basicInfo?.operationType,
                      basicInfo?.operationTypeLabel
                    )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Preview Mode Banner */}
      {previewMode && (
        <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <Eye className="h-5 w-5 text-blue-500 mr-2" />
            <div>
              <h3 className="font-medium">Viewing as Technician</h3>
              <p className="text-sm text-muted-foreground">
                This is how the SOP will appear to field technicians
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => setPreviewMode(false)}>
            Exit Preview
          </Button>
        </div>
      )}

      {/* SOP Summary Cards */}
      {!previewMode && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Total Tasks Card */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex flex-col">
            <div className="text-blue-500 text-sm font-medium mb-1">
              Total Tasks
            </div>
            <div className="text-2xl font-bold">
              {state.tasks?.length || 0} Tasks
            </div>
            <div className="mt-auto flex items-center text-sm text-blue-700">
              <Clock className="h-4 w-4 mr-1" />
              {formatDuration(calculateTotalDuration())} estimated
            </div>
          </div>

          {/* Required Personnel Card */}
          <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 flex flex-col">
            <div className="text-purple-500 text-sm font-medium mb-1">
              Required Personnel
            </div>
            <div className="text-2xl font-bold">
              {state.basicInfo?.requiredFieldforceCount || 1}
            </div>
            <div className="mt-auto flex items-center text-sm text-purple-700">
              <UserRound className="h-4 w-4 mr-1" />
              Field Technicians
            </div>
          </div>

          {/* Estimated Cost Card */}
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4 flex flex-col">
            <div className="text-emerald-500 text-sm font-medium mb-1">
              Estimated Cost
            </div>
            <div className="text-2xl font-bold">
              {currency} {estimatedLabourCost}
            </div>
            <div className="mt-auto flex items-center text-sm text-emerald-700">
              <Banknote className="h-4 w-4 mr-1" />
              Labor cost without materials
            </div>
          </div>
        </div>
      )}

      {/* Template Summary */}
      <div className="border rounded-lg overflow-hidden shadow-sm">
        <div className="p-4 border-b bg-slate-50">
          <h3 className="font-medium flex items-center">
            <FileText className="h-4 w-4 mr-2 text-blue-500" />
            Template Summary
          </h3>
          <p className="text-sm text-muted-foreground">
            Review your service order template
          </p>
        </div>

        {/* Basic Information Section */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("basicInfo")}
          >
            <div className="flex items-center">
              <FileText className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium">Basic Information</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge("basicInfo")}
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("basicInfo") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {isSectionExpanded("basicInfo") && (
            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              {renderBasicInfo()}
            </div>
          )}
        </div>

        {/* Safety Requirements Section */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("safety")}
          >
            <div className="flex items-center">
              <Shield className="h-5 w-5 text-red-500 mr-3" />
              <span className="font-medium">Safety Requirements</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge("safety")}
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("safety") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {isSectionExpanded("safety") && (
            <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground">Required PPE:</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {state.safety?.ppe?.map((item, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-slate-50"
                    >
                      {getPPEDisplay(item)}
                    </Badge>
                  ))}
                  {(!state.safety?.ppe || state.safety.ppe.length === 0) && (
                    <span className="text-sm text-muted-foreground">
                      None specified
                    </span>
                  )}
                </div>
              </div>

              {/* Add Safety Protocols section if you want to show them too */}
              <div>
                <p className="text-xs text-muted-foreground">
                  Safety Protocols:
                </p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {state.safety?.safetyProtocols?.map((item, index) => (
                    <Badge
                      key={index}
                      variant="outline"
                      className="bg-blue-50 border-blue-200"
                    >
                      {getSafetyProtocolDisplay(item)}
                    </Badge>
                  ))}
                  {(!state.safety?.safetyProtocols ||
                    state.safety.safetyProtocols.length === 0) && (
                    <span className="text-sm text-muted-foreground">
                      None specified
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Task Definition Section */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("tasks")}
          >
            <div className="flex items-center">
              <ClipboardList className="h-5 w-5 text-amber-500 mr-3" />
              <span className="font-medium">Task Definition</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge("tasks")}
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("tasks") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {isSectionExpanded("tasks") && (
            <div className="px-4 pb-4">
              {state.tasks?.length > 0 ? (
                <div className="space-y-3">
                  {state.tasks.map((task: any, index: number) => (
                    <div key={index} className="border rounded-md p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">
                            {index + 1}. {task.title}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {task.description}
                          </p>
                        </div>
                        <Badge className="bg-blue-50 text-blue-700 border-blue-100">
                          {task.duration} min
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No tasks defined
                </div>
              )}
            </div>
          )}
        </div>

        {/* Materials & Equipment Section */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("materials")}
          >
            <div className="flex items-center">
              <Package className="h-5 w-5 text-green-500 mr-3" />
              <span className="font-medium">Materials & Equipment</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge("materials")}
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("materials") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {isSectionExpanded("materials") && (
            <div className="px-4 pb-4">
              {state.materials?.length > 0 || state.equipment?.length > 0 ? (
                <div className="space-y-4">
                  {/* Materials */}
                  {state.materials?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">
                        Materials ({state.materials.length})
                      </h4>
                      <div className="space-y-2">
                        {state.materials.map((material: any, index: number) => (
                          <div key={index} className="border rounded-md p-3">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="font-medium">{material.name}</p>
                                <p className="text-sm text-muted-foreground">
                                  Quantity: {material.quantity}{" "}
                                  {material.size && `| Size: ${material.size}`}
                                </p>
                              </div>
                              {material.cost && (
                                <Badge variant="outline">
                                  {currency} {material.cost}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Equipment */}
                  {state.equipment?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-2">
                        Equipment ({state.equipment.length})
                      </h4>
                      <div className="space-y-2">
                        {state.equipment.map(
                          (equipment: any, index: number) => (
                            <div key={index} className="border rounded-md p-3">
                              <div className="flex justify-between items-start">
                                <div>
                                  <p className="font-medium">
                                    {equipment.name}
                                  </p>
                                </div>
                                <Badge
                                  variant="outline"
                                  className={`capitalize ${
                                    equipment.condition === "good"
                                      ? "border-green-500 text-green-700"
                                      : equipment.condition === "fair"
                                      ? "border-yellow-500 text-yellow-700"
                                      : equipment.condition === "poor"
                                      ? "border-red-500 text-red-700"
                                      : "border-blue-500 text-blue-700"
                                  }`}
                                >
                                  {equipment.condition}
                                </Badge>
                              </div>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No materials or equipment specified
                </div>
              )}
            </div>
          )}
        </div>

        {/* Readings & Data Section */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("readings")}
          >
            <div className="flex items-center">
              <BarChart2 className="h-5 w-5 text-blue-500 mr-3" />
              <span className="font-medium">Readings & Data</span>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge("readings")}
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("readings") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>

          {isSectionExpanded("readings") && (
            <div className="px-4 pb-4">
              {getReadings().length > 0 ? (
                <div className="space-y-3">
                  {state.readings.map((reading: any, index: number) => (
                    <div key={index} className="border rounded-md p-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">{reading.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Range: {reading.minimum} - {reading.maximum}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {reading.photoRequired == "Yes" && (
                            <Badge variant="outline" className="text-xs">
                              📷 Photo
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No readings specified
                </div>
              )}
            </div>
          )}
        </div>

        {/* Help & Resources Section (collapsible) */}
        <div className="border-b hover:bg-slate-50/50 transition-colors">
          <div
            className="flex items-center justify-between p-4 cursor-pointer"
            onClick={() => toggleSectionExpansion("resources")}
          >
            <div className="flex items-center">
              <HelpCircle className="h-5 w-5 text-purple-500 mr-3" />
              <span className="font-medium">Help & Resources</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100 rounded-md font-normal">
                {allResources.length} Resources
              </Badge>
              <ChevronRight
                className={`h-5 w-5 text-muted-foreground transition-transform ${
                  isSectionExpanded("resources") ? "rotate-90" : ""
                }`}
              />
            </div>
          </div>
          {isSectionExpanded("resources") && (
            <div className="px-4 pb-4 space-y-4">
              {/* Help Content if present */}
              {state.helpContent && (
                <div className="mb-2 text-sm text-muted-foreground">
                  <span className="font-medium">Help Content:</span>{" "}
                  {state.helpContent}
                </div>
              )}
              {/* Flat Resources List */}
              <h4 className="font-semibold mb-2">Resources</h4>
              {allResources.length === 0 ? (
                <div className="text-sm text-muted-foreground">
                  No resources added
                </div>
              ) : (
                <div className="space-y-2">
                  {allResources.map((resource, idx) => {
                    // Prefer file URL if present, else content/link
                    const resourceUrl =
                      resource.file ||
                      resource.content ||
                      resource.link ||
                      null;
                    return (
                      <div
                        key={resource.id || idx}
                        className="flex items-center justify-between border rounded px-3 py-2 bg-white"
                      >
                        <span>{resource.name}</span>
                        {resourceUrl ? (
                          <a
                            href={resourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm font-medium"
                          >
                            View Resource
                          </a>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            No link
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Template Validation Warnings */}
      {hasValidationWarnings && (
        <div className="border-amber-200 bg-amber-50 border rounded-lg p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            <h3 className="font-medium text-amber-800">
              Template Validation Warnings
            </h3>
          </div>

          <ul className="ml-6 list-disc text-sm space-y-1 text-amber-800">
            {!state.basicInfo?.sopName && <li>Template name is required</li>}
            {!state.basicInfo?.utilityType && <li>Utility type is required</li>}
            {!state.basicInfo?.type && (
              <li>Service order category is required</li>
            )}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        {!readOnly && onPrevious && (
          <Button
            variant="outline"
            onClick={onPrevious}
            className="flex items-center gap-2"
          >
            Previous Step
          </Button>
        )}

        <div className="flex gap-3 ml-auto">
          {/* {!readOnly && onSaveAsDraft && (
            <Button
              variant="outline"
              onClick={onSaveAsDraft}
              className="flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Save as Draft
            </Button>
          )} */}

          <Button
            onClick={handleSubmit}
            className="flex items-center gap-2"
            disabled={hasValidationWarnings && !readOnly}
          >
            <Check className="h-4 w-4" />
            {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SopReviewStep;
